import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Lock, LogOut, RefreshCw, Calendar, Mail, Users, Inbox, Download, Search, ArrowLeft, Settings } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import ContentTab from "@/pages/admin/ContentTab";

const TOKEN_KEY = "tr_admin_token";

const TYPE_LABEL = {
  general: "General",
  recruiting: "Recruiting",
  producer_coaching: "Producer Coaching",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function toCSV(rows, columns) {
  const head = columns.map((c) => `"${c.label}"`).join(",");
  const body = rows
    .map((r) =>
      columns
        .map((c) => {
          const v = c.get ? c.get(r) : r[c.key] ?? "";
          const s = String(v ?? "").replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    )
    .join("\n");
  return `${head}\n${body}`;
}

function downloadCSV(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div
      className="bg-card border border-border rounded-2xl p-6 flex items-start gap-4"
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center ${accent}`}
      >
        <Icon size={22} strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-foreground/55">
          {label}
        </div>
        <div className="font-serif text-4xl mt-1 leading-none">{value}</div>
      </div>
    </div>
  );
}

function LoginCard({ onLogin }) {
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post("/admin/login", { password });
      const token = res.data.token;
      localStorage.setItem(TOKEN_KEY, token);
      toast.success("Welcome back, Tammy.");
      onLogin(token);
    } catch (err) {
      toast.error("Incorrect admin password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-6">
      <div
        className="max-w-md w-full bg-card border border-border rounded-3xl p-10 text-center shadow-sm"
        data-testid="admin-login-card"
      >
        <div className="w-16 h-16 mx-auto rounded-full bg-primary/15 flex items-center justify-center">
          <Lock className="text-primary" size={26} strokeWidth={1.6} />
        </div>
        <h1 className="font-serif text-4xl mt-6">Admin Dashboard</h1>
        <p className="text-foreground/70 mt-4 leading-relaxed">
          Sign in to view bookings, inquiries, and newsletter subscribers.
        </p>
        <form onSubmit={onSubmit} className="mt-8 space-y-4 text-left">
          <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70">
            Admin password
          </Label>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="rounded-xl h-12"
            data-testid="admin-password-input"
            required
          />
          <Button
            type="submit"
            disabled={submitting}
            data-testid="admin-login-btn"
            className="w-full rounded-full bg-primary hover:bg-primary/90 btn-lift h-12"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </Button>
        </form>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs text-foreground/60 hover:text-primary mt-6"
        >
          <ArrowLeft size={14} /> Back to site
        </Link>
      </div>
    </div>
  );
}

const TABS = [
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "inquiries", label: "Inquiries", icon: Inbox },
  { id: "subscribers", label: "Subscribers", icon: Mail },
  { id: "content", label: "Content", icon: Settings },
];

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [activeTab, setActiveTab] = useState("bookings");
  const [summary, setSummary] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [inqTypeFilter, setInqTypeFilter] = useState("all");

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const fetchAll = async (tok = token) => {
    if (!tok) return;
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${tok}` };
      const [sumRes, apptRes, inqRes, subRes] = await Promise.all([
        api.get("/admin/summary", { headers }),
        api.get("/appointments", { headers }),
        api.get("/inquiries", { headers }),
        api.get("/newsletter/subscribers", { headers }),
      ]);
      setSummary(sumRes.data);
      setBookings(apptRes.data || []);
      setInquiries(inqRes.data || []);
      setSubscribers(subRes.data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load admin data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll(token);
  }, []);

  const handleLogin = (tok) => {
    setToken(tok);
    fetchAll(tok);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSummary(null);
    setBookings([]);
    setInquiries([]);
    setSubscribers([]);
  };

  if (!token) return <LoginCard onLogin={handleLogin} />;

  // ---- Filtered lists ----
  const q = search.trim().toLowerCase();
  const matches = (s) => !q || String(s || "").toLowerCase().includes(q);

  const filteredBookings = bookings.filter(
    (b) => matches(b.name) || matches(b.email) || matches(b.topic) || matches(b.message)
  );
  const filteredInquiries = inquiries.filter((i) => {
    const typeOk = inqTypeFilter === "all" || i.inquiry_type === inqTypeFilter;
    return (
      typeOk &&
      (matches(i.name) ||
        matches(i.email) ||
        matches(i.subject) ||
        matches(i.message) ||
        matches(i.current_brokerage))
    );
  });
  const filteredSubscribers = subscribers.filter(
    (s) => matches(s.name) || matches(s.email) || matches(s.source)
  );

  const exportCSV = () => {
    if (activeTab === "bookings") {
      const csv = toCSV(filteredBookings, [
        { key: "created_at", label: "Submitted", get: (r) => formatDate(r.created_at) },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "preferred_date", label: "Date" },
        { key: "preferred_time", label: "Time" },
        { key: "topic", label: "Topic" },
        { key: "message", label: "Message" },
        { key: "status", label: "Status" },
      ]);
      downloadCSV(`bookings-${Date.now()}.csv`, csv);
    } else if (activeTab === "inquiries") {
      const csv = toCSV(filteredInquiries, [
        { key: "created_at", label: "Submitted", get: (r) => formatDate(r.created_at) },
        { key: "inquiry_type", label: "Type", get: (r) => TYPE_LABEL[r.inquiry_type] || r.inquiry_type },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "phone", label: "Phone" },
        { key: "subject", label: "Subject" },
        { key: "experience_years", label: "Experience" },
        { key: "current_brokerage", label: "Brokerage" },
        { key: "message", label: "Message" },
      ]);
      downloadCSV(`inquiries-${Date.now()}.csv`, csv);
    } else {
      const csv = toCSV(filteredSubscribers, [
        { key: "created_at", label: "Subscribed", get: (r) => formatDate(r.created_at) },
        { key: "name", label: "Name" },
        { key: "email", label: "Email" },
        { key: "source", label: "Source" },
      ]);
      downloadCSV(`subscribers-${Date.now()}.csv`, csv);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-[#0F1E33] text-[#F5F8FB]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#93C5FD]">
              Tammy Richard · Admin
            </div>
            <h1 className="font-serif text-3xl md:text-4xl mt-1">Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => fetchAll()}
              variant="ghost"
              size="sm"
              className="text-[#F5F8FB] hover:bg-white/10 rounded-full"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
              <span className="ml-2">Refresh</span>
            </Button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-[#F5F8FB] hover:bg-white/10 rounded-full"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} />
              <span className="ml-2">Sign out</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
          <StatCard
            icon={Calendar}
            label="Bookings"
            value={summary?.appointments ?? "—"}
            accent="bg-primary/10 text-primary"
          />
          <StatCard
            icon={Inbox}
            label="Inquiries"
            value={summary?.inquiries ?? "—"}
            accent="bg-secondary/15 text-secondary"
          />
          <StatCard
            icon={Users}
            label="Recruiting"
            value={summary?.inquiries_by_type?.recruiting ?? 0}
            accent="bg-[#93C5FD]/25 text-primary"
          />
          <StatCard
            icon={Mail}
            label="Subscribers"
            value={summary?.subscribers ?? "—"}
            accent="bg-accent text-primary"
          />
        </div>

        {/* Tabs + Filters */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-5 border-b border-border">
            <div className="flex gap-1 bg-muted rounded-full p-1 w-fit">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    data-testid={`admin-tab-${t.id}`}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm rounded-full transition-all ${
                      active
                        ? "bg-primary text-primary-foreground shadow"
                        : "text-foreground/70 hover:text-foreground"
                    }`}
                  >
                    <Icon size={14} />
                    {t.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              {activeTab === "inquiries" && (
                <select
                  value={inqTypeFilter}
                  onChange={(e) => setInqTypeFilter(e.target.value)}
                  className="rounded-full h-10 px-4 text-sm border border-border bg-background"
                  data-testid="admin-inquiry-filter"
                >
                  <option value="all">All types</option>
                  <option value="general">General</option>
                  <option value="recruiting">Recruiting</option>
                  <option value="producer_coaching">Producer Coaching</option>
                </select>
              )}
              {activeTab !== "content" && (
                <>
                  <div className="relative">
                    <Search
                      size={14}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"
                    />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search…"
                      className="pl-9 rounded-full h-10 w-full sm:w-64"
                      data-testid="admin-search"
                    />
                  </div>
                  <Button
                    onClick={exportCSV}
                    variant="outline"
                    size="sm"
                    className="rounded-full h-10"
                    data-testid="admin-export-csv"
                  >
                    <Download size={14} />
                    <span className="ml-2">Export CSV</span>
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Tables */}
          {activeTab === "bookings" && (
            <BookingsTable rows={filteredBookings} loading={loading} />
          )}
          {activeTab === "inquiries" && (
            <InquiriesTable rows={filteredInquiries} loading={loading} />
          )}
          {activeTab === "subscribers" && (
            <SubscribersTable rows={filteredSubscribers} loading={loading} />
          )}
          {activeTab === "content" && (
            <ContentTab authHeaders={authHeaders} />
          )}
        </div>
      </main>
    </div>
  );
}

function EmptyState({ label }) {
  return (
    <div className="p-16 text-center text-foreground/55" data-testid="admin-empty">
      No {label} yet.
    </div>
  );
}

function LoadingRow() {
  return (
    <div className="p-16 text-center text-foreground/55" data-testid="admin-loading">
      Loading…
    </div>
  );
}

function BookingsTable({ rows, loading }) {
  if (loading) return <LoadingRow />;
  if (!rows.length) return <EmptyState label="bookings" />;
  return (
    <div className="overflow-x-auto" data-testid="admin-bookings-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-foreground/60 text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-5 py-3 font-medium">Submitted</th>
            <th className="text-left px-5 py-3 font-medium">Name</th>
            <th className="text-left px-5 py-3 font-medium">Email / Phone</th>
            <th className="text-left px-5 py-3 font-medium">Requested</th>
            <th className="text-left px-5 py-3 font-medium">Topic</th>
            <th className="text-left px-5 py-3 font-medium">Message</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border align-top">
              <td className="px-5 py-4 text-foreground/60 whitespace-nowrap">
                {formatDate(r.created_at)}
              </td>
              <td className="px-5 py-4 font-medium">{r.name}</td>
              <td className="px-5 py-4">
                <a href={`mailto:${r.email}`} className="text-primary hover:underline">
                  {r.email}
                </a>
                {r.phone && (
                  <div className="text-foreground/60 text-xs mt-0.5">{r.phone}</div>
                )}
              </td>
              <td className="px-5 py-4 whitespace-nowrap">
                {r.preferred_date}
                <div className="text-foreground/60 text-xs">{r.preferred_time}</div>
              </td>
              <td className="px-5 py-4">{r.topic}</td>
              <td className="px-5 py-4 text-foreground/70 max-w-xs">
                <div className="line-clamp-3 whitespace-pre-wrap">{r.message || "—"}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InquiriesTable({ rows, loading }) {
  if (loading) return <LoadingRow />;
  if (!rows.length) return <EmptyState label="inquiries" />;
  return (
    <div className="overflow-x-auto" data-testid="admin-inquiries-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-foreground/60 text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-5 py-3 font-medium">Submitted</th>
            <th className="text-left px-5 py-3 font-medium">Type</th>
            <th className="text-left px-5 py-3 font-medium">Name</th>
            <th className="text-left px-5 py-3 font-medium">Email / Phone</th>
            <th className="text-left px-5 py-3 font-medium">Details</th>
            <th className="text-left px-5 py-3 font-medium">Message</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border align-top">
              <td className="px-5 py-4 text-foreground/60 whitespace-nowrap">
                {formatDate(r.created_at)}
              </td>
              <td className="px-5 py-4">
                <span className="inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-primary/10 text-primary">
                  {TYPE_LABEL[r.inquiry_type] || r.inquiry_type}
                </span>
              </td>
              <td className="px-5 py-4 font-medium">{r.name}</td>
              <td className="px-5 py-4">
                <a href={`mailto:${r.email}`} className="text-primary hover:underline">
                  {r.email}
                </a>
                {r.phone && (
                  <div className="text-foreground/60 text-xs mt-0.5">{r.phone}</div>
                )}
              </td>
              <td className="px-5 py-4 text-foreground/70 max-w-xs">
                {r.subject && <div className="font-medium">{r.subject}</div>}
                {r.current_brokerage && (
                  <div className="text-xs text-foreground/60">
                    Brokerage: {r.current_brokerage}
                  </div>
                )}
                {r.experience_years && (
                  <div className="text-xs text-foreground/60">
                    Experience: {r.experience_years}
                  </div>
                )}
                {!r.subject && !r.current_brokerage && !r.experience_years && "—"}
              </td>
              <td className="px-5 py-4 text-foreground/70 max-w-md">
                <div className="line-clamp-3 whitespace-pre-wrap">{r.message || "—"}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SubscribersTable({ rows, loading }) {
  if (loading) return <LoadingRow />;
  if (!rows.length) return <EmptyState label="subscribers" />;
  return (
    <div className="overflow-x-auto" data-testid="admin-subscribers-table">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-foreground/60 text-[11px] uppercase tracking-wider">
          <tr>
            <th className="text-left px-5 py-3 font-medium">Subscribed</th>
            <th className="text-left px-5 py-3 font-medium">Name</th>
            <th className="text-left px-5 py-3 font-medium">Email</th>
            <th className="text-left px-5 py-3 font-medium">Source</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-border">
              <td className="px-5 py-4 text-foreground/60 whitespace-nowrap">
                {formatDate(r.created_at)}
              </td>
              <td className="px-5 py-4 font-medium">{r.name}</td>
              <td className="px-5 py-4">
                <a href={`mailto:${r.email}`} className="text-primary hover:underline">
                  {r.email}
                </a>
              </td>
              <td className="px-5 py-4 text-foreground/70">{r.source || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
